import {RepositoryLabelToken} from '@github-ui/label-token/RepositoryLabelToken'
import {Link, Tooltip} from '@primer/react'
import {clsx} from 'clsx'
import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback, useMemo, type ReactNode} from 'react'

import styles from './LabelsList.module.css'

/**
 * This interface is the same as that of @github-ui/item-picker/label's Label
 * but it doesn't seem appropriate to couple this list to the picker
 * in that way
 */
export interface Label {
  readonly id: string
  readonly url: string
  readonly name: string
  readonly nameHTML: string
  readonly color: string
  readonly description: string | null | undefined
  /**
   * Set when the label has been archived. Optional so producers that don't yet
   * select the field (or predate label archiving) stay assignable.
   */
  readonly archivedAt?: string | null
}

export type LabelsListProps = {
  labels: Label[]
  testId?: string
  /** We display this string when the list of labels is empty, defaults to 'No labels' */
  emptyText?: ReactNode
}

/**
 *
 * A list of label tokens sorted by name.
 * This component is used in the Issues and Pull Requests sidebars.
 * If we find either one needs to diverge too much we should consider
 * making two separate components instead of trying to make this one fit both.
 *
 */
export function LabelsList({labels, testId, emptyText = 'No labels'}: LabelsListProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const repoIssuesPath = useMemo(() => {
    if (labels.length === 0 || !labels[0]) return ''
    return `${labels[0].url.slice(0, labels[0].url.lastIndexOf('/labels/'))}/issues`
  }, [labels])

  const labelIndexUrl = useCallback(
    (label: Label) => `${repoIssuesPath}?q=${encodeURIComponent(`state:open label:"${label.name}"`)}`,
    [repoIssuesPath],
  )

  return (
    <div tabIndex={-1} data-testid={testId} className={styles.labelsListContainer}>
      {labels.length > 0
        ? [...labels]
            .sort((a, b) => (a.name === b.name ? 0 : a.name > b.name ? 1 : -1))
            .map(label => (
              <Tooltip
                text={label.description ?? ''}
                type="description"
                aria-label={label.description ?? ''}
                key={label.id}
                className={clsx(
                  styles.labelDescriptionTooltip,
                  label.description ? styles.tooltipVisible : styles.tooltipHidden,
                )}
              >
                <Link
                  href={labelIndexUrl(label)}
                  key={label.id}
                  muted
                  aria-describedby={`${label.id}-tooltip`}
                  className={styles.labelLink}
                  onClick={() => {
                    sendAnalyticsEvent('analytics.click', 'ISSUE_SIDEBAR_LABEL_CLICK')
                  }}
                  onContextMenu={() => {
                    sendAnalyticsEvent('analytics.click', 'ISSUE_SIDEBAR_LABEL_RIGHT_CLICK')
                  }}
                >
                  <RepositoryLabelToken
                    label={label}
                    key={label.id}
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      cursor: 'pointer',
                      maxWidth: '100%',
                    }}
                  />
                  <span className="sr-only" id={`${label.id}-tooltip`}>
                    {label.description ?? ''}
                  </span>
                </Link>
              </Tooltip>
            ))
        : emptyText}
    </div>
  )
}
